import { useRouter } from "expo-router";
import { useState } from "react";
import {
  ActivityIndicator,
  FlatList,
  Pressable,
  StyleSheet,
  TouchableOpacity,
  View,
} from "react-native";
import {
  Badge,
  Button,
  Card,
  Input,
  Screen,
  Text,
  useTheme,
  useThemedStyles,
} from "~/components/ui";
import { useRecipesFiltered, type Recipe } from "~/hooks/useRecipes";
import { radius, spacing, type ThemeColors } from "~/styles/tokens";

const DIFFICULTY_VARIANT: Record<string, "success" | "warning" | "error"> = {
  easy: "success",
  medium: "warning",
  hard: "error",
};

const FILTERS = [
  { value: "", label: "All" },
  { value: "easy", label: "Easy" },
  { value: "medium", label: "Medium" },
  { value: "hard", label: "Hard" },
];

function RecipeRow({
  recipe,
  onPress,
}: {
  recipe: Recipe;
  onPress: () => void;
}) {
  const styles = useStyles();
  return (
    <TouchableOpacity
      accessibilityRole="button"
      accessibilityLabel={recipe.title}
      onPress={onPress}
      activeOpacity={0.85}
    >
      <Card elevation="raised" style={styles.recipeCard}>
        <View style={styles.recipeHeader}>
          <View style={styles.recipeThumb}>
            <Text style={styles.recipeEmoji}>{recipe.emoji || "🍞"}</Text>
          </View>
          <View style={styles.recipeMeta}>
            <Text variant="title" numberOfLines={1}>
              {recipe.title}
            </Text>
            <Text variant="caption" numberOfLines={2} style={styles.recipeDesc}>
              {recipe.description}
            </Text>
            <View style={styles.badges}>
              <Badge
                label={
                  recipe.difficulty.charAt(0).toUpperCase() +
                  recipe.difficulty.slice(1)
                }
                variant={DIFFICULTY_VARIANT[recipe.difficulty] ?? "default"}
              />
              <Badge label={`${recipe.prepTimeMinutes}m prep`} />
              <Badge label={`${recipe.fermentTimeHours}h ferment`} />
            </View>
          </View>
        </View>
      </Card>
    </TouchableOpacity>
  );
}

export default function RecipesScreen() {
  const router = useRouter();
  const { colors } = useTheme();
  const styles = useStyles();
  const [query, setQuery] = useState("");
  const [difficulty, setDifficulty] = useState("");
  const {
    data: recipes,
    loading,
    error,
    refetch,
  } = useRecipesFiltered({
    query: query.trim(),
    difficulty,
  });

  if (loading) {
    return (
      <Screen>
        <View style={styles.center}>
          <ActivityIndicator size="large" color={colors.primary} />
          <Text variant="caption" style={styles.centerNote}>
            Loading recipes…
          </Text>
        </View>
      </Screen>
    );
  }

  if (error) {
    return (
      <Screen>
        <View style={styles.center}>
          <Text style={styles.bigEmoji}>😕</Text>
          <Text variant="title" align="center">
            Something went wrong
          </Text>
          <Text variant="body" align="center" color={colors.muted}>
            {error}
          </Text>
          <Button
            label="Try again"
            onPress={() => void refetch()}
            variant="secondary"
            style={styles.centerNote}
          />
        </View>
      </Screen>
    );
  }

  return (
    <Screen padded={false}>
      <View style={styles.pageHeader}>
        <Text variant="heading">Recipes</Text>
        <Text variant="caption">{recipes.length} sourdough goodies</Text>
        <Input
          placeholder="Search by title or ingredient"
          value={query}
          onChangeText={setQuery}
          autoCapitalize="none"
          style={styles.search}
        />
        <View style={styles.filters} accessibilityRole="radiogroup">
          {FILTERS.map((filter) => {
            const active = difficulty === filter.value;
            return (
              <Pressable
                key={filter.value || "all"}
                onPress={() => setDifficulty(filter.value)}
                accessibilityRole="radio"
                accessibilityState={{ checked: active }}
                style={[styles.chip, active && styles.chipActive]}
              >
                <Text
                  variant="label"
                  style={[styles.chipText, active && styles.chipTextActive]}
                >
                  {filter.label}
                </Text>
              </Pressable>
            );
          })}
        </View>
      </View>
      {recipes.length === 0 ? (
        <View style={styles.center}>
          <Text style={styles.bigEmoji}>🧺</Text>
          <Text variant="title" align="center">
            No recipes yet
          </Text>
          <Text variant="body" align="center" color={colors.muted}>
            Check back soon — we&apos;re still baking some up.
          </Text>
        </View>
      ) : (
        <FlatList
          data={recipes}
          keyExtractor={(item) => item.id}
          contentContainerStyle={styles.list}
          onRefresh={() => void refetch()}
          refreshing={loading}
          showsVerticalScrollIndicator={false}
          renderItem={({ item }) => (
            <RecipeRow
              recipe={item}
              onPress={() =>
                router.push({
                  pathname: "/recipe/[id]",
                  params: { id: item.id },
                })
              }
            />
          )}
        />
      )}
    </Screen>
  );
}

const useStyles = () => useThemedStyles(makeStyles);

const makeStyles = (c: ThemeColors) =>
  StyleSheet.create({
    pageHeader: {
      paddingHorizontal: spacing.md,
      paddingTop: spacing.lg,
      paddingBottom: spacing.md,
      gap: spacing.xs,
    },
    search: {
      marginTop: spacing.sm,
      marginBottom: 0,
    },
    filters: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.xs,
      marginTop: spacing.xs,
    },
    chip: {
      borderRadius: radius.full,
      paddingHorizontal: spacing.md,
      paddingVertical: spacing.xs + 2,
      backgroundColor: c.surface,
      borderWidth: 1,
      borderColor: c.border,
    },
    chipActive: {
      backgroundColor: c.primary,
      borderColor: c.primary,
    },
    chipText: {
      color: c.heading,
    },
    chipTextActive: {
      color: c.primaryForeground,
    },
    list: {
      paddingHorizontal: spacing.md,
      paddingTop: spacing.xs,
      paddingBottom: spacing.xxl,
      gap: spacing.sm,
    },
    recipeCard: {
      padding: spacing.md,
    },
    recipeHeader: {
      flexDirection: "row",
      alignItems: "flex-start",
      gap: spacing.md,
    },
    recipeThumb: {
      width: 52,
      height: 52,
      borderRadius: radius.lg,
      backgroundColor: c.background,
      alignItems: "center",
      justifyContent: "center",
    },
    recipeEmoji: {
      fontSize: 28,
    },
    recipeMeta: {
      flex: 1,
      gap: 2,
    },
    recipeDesc: {
      marginBottom: spacing.xs,
    },
    badges: {
      flexDirection: "row",
      flexWrap: "wrap",
      gap: spacing.xs,
    },
    center: {
      flex: 1,
      alignItems: "center",
      justifyContent: "center",
      padding: spacing.xl,
      gap: spacing.sm,
    },
    centerNote: {
      marginTop: spacing.sm,
    },
    bigEmoji: {
      fontSize: 48,
      lineHeight: 56,
    },
  });
